import { debounce } from '@theme/utilities';

/**
 * Helpcenter Page Component
 * Handles search filtering, category filtering, and deep linking via URL hash for FAQ items.
 *
 * @class HelpcenterPageComponent
 * @extends {HTMLElement}
 */
class HelpcenterPageComponent extends HTMLElement {
  #controller = new AbortController();
  #searchDebounced = debounce(this.filterHelpcenterItems.bind(this), 200);
  /** @type {string | null} */
  #activeCategory = null;

  /**
   * Get the search input element (internal).
   * @returns {HTMLInputElement | null}
   */
  get searchInput() {
    return this.querySelector('[data-helpcenter-search-input]');
  }

  /**
   * Get the reset button element (internal).
   * @returns {HTMLButtonElement | null}
   */
  get resetButton() {
    return this.querySelector('[data-helpcenter-search-reset]');
  }

  /**
   * Get the no results element (internal).
   * @returns {HTMLElement | null}
   */
  get noResultsElement() {
    return this.querySelector('[data-helpcenter-no-results]');
  }

  /**
   * Get all FAQ items.
   * @returns {NodeListOf<HTMLElement>}
   */
  get helpcenterItems() {
    return this.querySelectorAll('.helpcenter-item');
  }

  /**
   * Get all category groups.
   * @returns {NodeListOf<HTMLElement>}
   */
  get categoryGroups() {
    return this.querySelectorAll('.helpcenter-category-group');
  }

  /**
   * Get the currently active category ID.
   * @returns {string | null}
   */
  get activeCategory() {
    return this.#activeCategory;
  }

  connectedCallback() {
    const { signal } = this.#controller;

    // Set up internal search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.#handleSearchInput, { signal });
    }

    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.#handleReset, { signal });
    }

    // Handle deep linking on load
    this.#openHelpcenterItemFromHash();

    // Listen for hash changes
    window.addEventListener('hashchange', this.#handleHashChange, { signal });

    // Update hash when FAQ is toggled
    this.addEventListener('toggle', this.#handleHelpcenterToggle, { signal, capture: true });

    // Listen for external search events
    document.addEventListener('helpcenter-search', this.#handleExternalSearch, { signal });

    // Listen for category selection events
    document.addEventListener('helpcenter-category-select', this.#handleCategorySelect, { signal });

    // Select first category by default (triggered after grid component initializes)
    document.addEventListener('helpcenter-category-grid-ready', this.#handleCategoryGridReady, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handle search input changes (internal).
   * @param {Event} event - The input event.
   */
  #handleSearchInput = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#searchDebounced(input.value);
  };

  /**
   * Handle reset button click (internal).
   */
  #handleReset = () => {
    if (this.searchInput) {
      this.searchInput.value = '';
      this.searchInput.focus();
    }
    this.filterHelpcenterItems('');
  };

  /**
   * Handle external search events from helpcenter-search-component.
   * @param {CustomEvent} event - The custom search event.
   */
  #handleExternalSearch = (event) => {
    const { query, noResultsElement } = event.detail;
    const visibleCount = this.filterHelpcenterItems(query, noResultsElement);
    return visibleCount;
  };

  /**
   * Handle category selection events from helpcenter-category-grid-component.
   * @param {CustomEvent} event - The custom category select event.
   */
  #handleCategorySelect = (event) => {
    const { categoryId } = event.detail;
    this.filterByCategory(categoryId);
  };

  /**
   * Handle category grid ready event to select first category.
   * @param {CustomEvent} event - The custom ready event.
   */
  #handleCategoryGridReady = (event) => {
    const { firstCategoryId } = event.detail;
    if (firstCategoryId && !this.#activeCategory) {
      this.filterByCategory(firstCategoryId);
    }
  };

  /**
   * Handle hash changes in the URL.
   */
  #handleHashChange = () => {
    this.#openHelpcenterItemFromHash();
  };

  /**
   * Handle FAQ toggle events to update URL hash.
   * @param {Event} event - The toggle event.
   */
  #handleHelpcenterToggle = (event) => {
    const target = /** @type {HTMLDetailsElement} */ (event.target);

    if (!(target instanceof HTMLDetailsElement)) return;

    const helpcenterItem = target.closest('.helpcenter-item');
    if (!helpcenterItem) return;

    const itemId = helpcenterItem.id;
    if (!itemId) return;

    if (target.open) {
      // Update URL hash when helpcenter item is opened (without triggering scroll)
      history.replaceState(null, '', `#${itemId}`);
    }
  };

  /**
   * Filter FAQ items by category.
   * @param {string | null} categoryId - The category ID to filter by.
   */
  filterByCategory(categoryId) {
    this.#activeCategory = categoryId;

    // Show/hide category groups based on selection
    this.categoryGroups.forEach((group) => {
      const groupCategoryId = /** @type {HTMLElement} */ (group).dataset.categoryId;
      if (categoryId === null || categoryId === '' || groupCategoryId === categoryId) {
        group.hidden = false;
      } else {
        group.hidden = true;
        // Close all FAQs in hidden groups
        group.querySelectorAll('details').forEach((details) => {
          details.open = false;
        });
      }
    });

    // Reset individual FAQ item visibility within visible groups
    this.helpcenterItems.forEach((item) => {
      const itemCategoryId = item.dataset.helpcenterCategory;
      if (categoryId === null || categoryId === '' || itemCategoryId === categoryId) {
        item.hidden = false;
      } else {
        item.hidden = true;
      }
    });
  }

  /**
   * Filter FAQ items based on search query.
   * @param {string} query - The search query.
   * @param {HTMLElement} [externalNoResults] - External no results element.
   * @returns {number} - Number of visible items.
   */
  filterHelpcenterItems(query, externalNoResults) {
    const normalizedQuery = query.toLowerCase().trim();
    let visibleCount = 0;
    const activeCategory = this.#activeCategory;

    // Filter individual FAQ items
    this.helpcenterItems.forEach((item) => {
      const question = item.dataset.helpcenterQuestion?.toLowerCase() || '';
      const answer = item.dataset.helpcenterAnswer?.toLowerCase() || '';
      const itemCategoryId = item.dataset.helpcenterCategory;

      // Check if item matches search query
      const matchesQuery = normalizedQuery === '' || question.includes(normalizedQuery) || answer.includes(normalizedQuery);
      
      // Check if item matches active category (if any)
      const matchesCategory = activeCategory === null || activeCategory === '' || itemCategoryId === activeCategory;

      if (matchesQuery && matchesCategory) {
        item.hidden = false;
        visibleCount++;
      } else {
        item.hidden = true;
        // Close hidden items
        const details = item.querySelector('details');
        if (details) {
          details.open = false;
        }
      }
    });

    // Update category group visibility
    this.categoryGroups.forEach((group) => {
      const groupCategoryId = /** @type {HTMLElement} */ (group).dataset.categoryId;
      const matchesCategory = activeCategory === null || activeCategory === '' || groupCategoryId === activeCategory;
      
      if (!matchesCategory) {
        group.hidden = true;
      } else {
        const visibleItems = group.querySelectorAll('.helpcenter-item:not([hidden])');
        group.hidden = visibleItems.length === 0;
      }
    });

    // Show/hide no results message (internal)
    if (this.noResultsElement) {
      this.noResultsElement.hidden = visibleCount > 0 || normalizedQuery === '';
    }

    // Show/hide no results message (external)
    if (externalNoResults) {
      externalNoResults.hidden = visibleCount > 0 || normalizedQuery === '';
    }

    return visibleCount;
  }

  /**
   * Open FAQ item based on URL hash.
   */
  #openHelpcenterItemFromHash() {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    // Find the helpcenter item by ID
    const helpcenterItem = this.querySelector(`#${CSS.escape(hash)}`);
    if (!helpcenterItem) return;

    // Open the details element
    const details = helpcenterItem.querySelector('details');
    if (details) {
      details.open = true;

      // Scroll the item into view
      requestAnimationFrame(() => {
        helpcenterItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }
}

/**
 * Helpcenter Search Component
 * Standalone search input with autocomplete suggestions.
 *
 * @class HelpcenterSearchComponent
 * @extends {HTMLElement}
 */
class HelpcenterSearchComponent extends HTMLElement {
  #controller = new AbortController();
  #searchDebounced = debounce(this.#updateSuggestions.bind(this), 150);
  #selectedIndex = -1;

  /**
   * Get the search input element.
   * @returns {HTMLInputElement | null}
   */
  get searchInput() {
    return this.querySelector('[data-helpcenter-search-input]');
  }

  /**
   * Get the reset button element.
   * @returns {HTMLButtonElement | null}
   */
  get resetButton() {
    return this.querySelector('[data-helpcenter-search-reset]');
  }

  /**
   * Get the no results element.
   * @returns {HTMLElement | null}
   */
  get noResultsElement() {
    return this.querySelector('[data-helpcenter-no-results]');
  }

  /**
   * Get the suggestions element.
   * @returns {HTMLElement | null}
   */
  get suggestionsElement() {
    return this.querySelector('[data-helpcenter-suggestions]');
  }

  /**
   * Get all FAQ items from the page.
   * @returns {Array<{id: string, question: string, answer: string, category: string, element: HTMLElement}>}
   */
  get helpcenterItems() {
    const helpcenterComponent = document.querySelector('helpcenter-page-component');
    if (!helpcenterComponent) return [];

    const items = helpcenterComponent.querySelectorAll('.helpcenter-item');
    return Array.from(items).map((item) => {
      const el = /** @type {HTMLElement} */ (item);
      return {
        id: el.id,
        question: el.dataset.helpcenterQuestion || '',
        answer: el.dataset.helpcenterAnswer || '',
        category: el.dataset.helpcenterCategory || '',
        element: el,
      };
    });
  }

  connectedCallback() {
    const { signal } = this.#controller;

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.#handleSearchInput, { signal });
      this.searchInput.addEventListener('keydown', this.#handleKeyDown, { signal });
      this.searchInput.addEventListener('focus', this.#handleFocus, { signal });
    }

    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.#handleReset, { signal });
    }

    // Close suggestions when clicking outside
    document.addEventListener('click', this.#handleClickOutside, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handle search input changes.
   * @param {Event} event - The input event.
   */
  #handleSearchInput = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#searchDebounced(input.value);
  };

  /**
   * Handle input focus.
   */
  #handleFocus = () => {
    const query = this.searchInput?.value || '';
    if (query.trim().length > 0) {
      this.#updateSuggestions(query);
    }
  };

  /**
   * Handle keyboard navigation.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    const suggestions = this.suggestionsElement?.querySelectorAll('.helpcenter-search__suggestion') || [];
    const suggestionsArray = Array.from(suggestions);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#selectedIndex = Math.min(this.#selectedIndex + 1, suggestionsArray.length - 1);
        this.#updateSelectedSuggestion(suggestionsArray);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#selectedIndex = Math.max(this.#selectedIndex - 1, -1);
        this.#updateSelectedSuggestion(suggestionsArray);
        break;

      case 'Enter':
        if (this.#selectedIndex >= 0 && suggestionsArray[this.#selectedIndex]) {
          event.preventDefault();
          const selectedEl = /** @type {HTMLElement} */ (suggestionsArray[this.#selectedIndex]);
          const itemId = selectedEl.dataset.helpcenterId;
          if (itemId) {
            this.#selectSuggestion(itemId);
          }
        } else {
          // Filter all FAQs with current query
          this.#triggerSearch(this.searchInput?.value || '');
          this.#hideSuggestions();
        }
        break;

      case 'Escape':
        this.#hideSuggestions();
        break;
    }
  };

  /**
   * Handle click outside to close suggestions.
   * @param {Event} event - The click event.
   */
  #handleClickOutside = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.#hideSuggestions();
    }
  };

  /**
   * Handle reset button click.
   */
  #handleReset = () => {
    if (this.searchInput) {
      this.searchInput.value = '';
      this.searchInput.focus();
    }
    this.#hideSuggestions();
    this.#triggerSearch('');
  };

  /**
   * Update suggestions based on query.
   * @param {string} query - The search query.
   */
  #updateSuggestions(query) {
    const normalizedQuery = query.toLowerCase().trim();

    if (normalizedQuery.length === 0) {
      this.#hideSuggestions();
      this.#triggerSearch('');
      return;
    }

    const matches = this.helpcenterItems.filter((item) => {
      const question = item.question.toLowerCase();
      const answer = item.answer.toLowerCase();
      return question.includes(normalizedQuery) || answer.includes(normalizedQuery);
    });

    if (matches.length === 0) {
      this.#hideSuggestions();
      this.#triggerSearch(query);
      return;
    }

    // Limit to top 5 suggestions
    const topMatches = matches.slice(0, 5);

    this.#renderSuggestions(topMatches, normalizedQuery);
    this.#showSuggestions();

    // Also filter the FAQ list
    this.#triggerSearch(query);
  }

  /**
   * Render suggestions in the dropdown.
   * @param {Array<{id: string, question: string, category: string}>} matches - Matching FAQ items.
   * @param {string} query - The search query for highlighting.
   */
  #renderSuggestions(matches, query) {
    if (!this.suggestionsElement) return;

    this.suggestionsElement.innerHTML = matches
      .map(
        (match, index) => `
        <li
          class="helpcenter-search__suggestion"
          role="option"
          data-helpcenter-id="${match.id}"
          tabindex="-1"
          aria-selected="${index === this.#selectedIndex}"
        >
          <p class="helpcenter-search__suggestion-question">${this.#highlightMatch(match.question, query)}</p>
        </li>
      `
      )
      .join('');

    // Add click handlers to suggestions
    this.suggestionsElement.querySelectorAll('.helpcenter-search__suggestion').forEach((suggestion) => {
      suggestion.addEventListener('click', () => {
        const itemId = /** @type {HTMLElement} */ (suggestion).dataset.helpcenterId;
        if (itemId) {
          this.#selectSuggestion(itemId);
        }
      });
    });

    this.#selectedIndex = -1;
  }

  /**
   * Highlight matching text in a string.
   * @param {string} text - The text to highlight.
   * @param {string} query - The query to highlight.
   * @returns {string} - HTML with highlighted matches.
   */
  #highlightMatch(text, query) {
    if (!query) return text;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark class="helpcenter-search__suggestion-highlight">$1</mark>');
  }

  /**
   * Update selected suggestion styling.
   * @param {Element[]} suggestions - Array of suggestion elements.
   */
  #updateSelectedSuggestion(suggestions) {
    suggestions.forEach((suggestion, index) => {
      suggestion.setAttribute('aria-selected', String(index === this.#selectedIndex));
    });

    // Scroll selected into view
    if (this.#selectedIndex >= 0) {
      const selected = suggestions[this.#selectedIndex];
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }

  /**
   * Select a suggestion and navigate to the helpcenter item.
   * @param {string} itemId - The helpcenter item ID.
   */
  #selectSuggestion(itemId) {
    this.#hideSuggestions();

    // Find and open the helpcenter item
    const helpcenterItem = document.getElementById(itemId);
    if (helpcenterItem) {
      const details = helpcenterItem.querySelector('details');
      if (details) {
        details.open = true;
      }

      // Update URL hash
      history.replaceState(null, '', `#${itemId}`);

      // Scroll to the item
      requestAnimationFrame(() => {
        helpcenterItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      // Clear the search and show all FAQs
      if (this.searchInput) {
        this.searchInput.value = '';
      }
      this.#triggerSearch('');
    }
  }

  /**
   * Show the suggestions dropdown.
   */
  #showSuggestions() {
    if (this.suggestionsElement) {
      this.suggestionsElement.hidden = false;
    }
    if (this.searchInput) {
      this.searchInput.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * Hide the suggestions dropdown.
   */
  #hideSuggestions() {
    if (this.suggestionsElement) {
      this.suggestionsElement.hidden = true;
    }
    if (this.searchInput) {
      this.searchInput.setAttribute('aria-expanded', 'false');
    }
    this.#selectedIndex = -1;
  }

  /**
   * Trigger search on FAQ components.
   * @param {string} query - The search query.
   */
  #triggerSearch(query) {
    // Dispatch custom event that FAQ components listen for
    document.dispatchEvent(
      new CustomEvent('helpcenter-search', {
        detail: {
          query,
          noResultsElement: this.noResultsElement,
        },
      })
    );
  }
}

/**
 * Helpcenter Category Grid Component
 * Displays FAQ categories in a grid and handles category selection.
 *
 * @class HelpcenterCategoryGridComponent
 * @extends {HTMLElement}
 */
class HelpcenterCategoryGridComponent extends HTMLElement {
  #controller = new AbortController();

  /**
   * Get all category card buttons.
   * @returns {NodeListOf<HTMLButtonElement>}
   */
  get categoryCards() {
    return this.querySelectorAll('.helpcenter-category-card');
  }

  connectedCallback() {
    const { signal } = this.#controller;

    // Add click handlers to category cards
    this.categoryCards.forEach((card) => {
      card.addEventListener('click', this.#handleCardClick, { signal });
    });

    // Dispatch ready event with first category ID
    requestAnimationFrame(() => {
      const firstCard = this.querySelector('.helpcenter-category-card');
      if (firstCard) {
        const firstCategoryId = /** @type {HTMLElement} */ (firstCard).dataset.categoryId;
        document.dispatchEvent(
          new CustomEvent('helpcenter-category-grid-ready', {
            detail: { firstCategoryId },
          })
        );
      }
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handle category card click.
   * @param {Event} event - The click event.
   */
  #handleCardClick = (event) => {
    const card = /** @type {HTMLElement} */ (event.currentTarget);
    const categoryId = card.dataset.categoryId;

    if (!categoryId) return;

    // Update active states
    this.categoryCards.forEach((c) => {
      c.classList.remove('helpcenter-category-card--active');
      c.setAttribute('aria-pressed', 'false');
    });

    card.classList.add('helpcenter-category-card--active');
    card.setAttribute('aria-pressed', 'true');

    // Dispatch category select event
    document.dispatchEvent(
      new CustomEvent('helpcenter-category-select', {
        detail: { categoryId },
      })
    );
  };
}

if (!customElements.get('helpcenter-page-component')) {
  customElements.define('helpcenter-page-component', HelpcenterPageComponent);
}

if (!customElements.get('helpcenter-search-component')) {
  customElements.define('helpcenter-search-component', HelpcenterSearchComponent);
}

if (!customElements.get('helpcenter-category-grid-component')) {
  customElements.define('helpcenter-category-grid-component', HelpcenterCategoryGridComponent);
}
